import { useEffect, useState } from "react";
import api, { formatApiError } from "../api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, ShieldCheck, Crown } from "lucide-react";

const emptyForm = { name: "", email: "", password: "", active: true, admin_level: "operacional" };

export default function AdminsPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState(null);
  const [editingId, setEditingId] = useState(null);

  const isMaster = user?.admin_level === "master";

  const load = async () => {
    try {
      const { data } = await api.get("/admins");
      setAdmins(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setForm({ ...emptyForm });
    setEditingId(null);
  };
  const openEdit = (a) => {
    setForm({
      name: a.name,
      email: a.email,
      password: "",
      active: a.active,
      admin_level: a.admin_level || "operacional",
    });
    setEditingId(a.id);
  };

  const submit = async () => {
    try {
      if (editingId) {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        await api.put(`/admins/${editingId}`, payload);
        toast.success("Recepcionista atualizada");
      } else {
        await api.post("/admins", form);
        toast.success("Recepcionista cadastrada");
      }
      setForm(null);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Excluir ${a.name}?`)) return;
    try {
      await api.delete(`/admins/${a.id}`);
      toast.success("Excluída");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gray-500">Equipe</div>
          <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">Recepcionistas</h1>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            <strong>Master</strong> tem controle total (fila, profissionais, categorias e gestão de recepcionistas).
            <strong> Operacional</strong> opera a fila e cadastra profissionais, mas não gerencia recepcionistas.
            Cada ação da recepção fica registrada no histórico com o nome de quem a fez.
          </p>
        </div>
        {isMaster && (
          <button
            data-testid="btn-new-admin"
            onClick={openNew}
            className="inline-flex items-center gap-2 rounded-full bg-black px-4 py-2 text-sm font-semibold text-white hover:-translate-y-[1px] transition-transform"
          >
            <Plus size={16} /> Nova recepcionista
          </button>
        )}
      </div>

      {!isMaster && (
        <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          Somente a operadora master pode cadastrar ou editar recepcionistas.
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="hidden md:table-cell px-4 py-3">E-mail</th>
              <th className="px-4 py-3">Nível</th>
              <th className="px-4 py-3">Ativa</th>
              {isMaster && <th className="px-4 py-3 text-right">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {admins.length === 0 && (
              <tr>
                <td colSpan={isMaster ? 5 : 4} className="py-10 text-center text-gray-400">Nenhuma recepcionista cadastrada</td>
              </tr>
            )}
            {admins.map((a) => {
              const isMe = user?.id === a.id;
              const level = a.admin_level || "operacional";
              return (
                <tr key={a.id} className="border-t border-gray-100" data-testid={`admin-row-${a.id}`}>
                  <td className="px-4 py-3 font-medium">
                    <span className="inline-flex items-center gap-2">
                      {a.name}
                      {isMe && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-pink-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-pink-700">
                          <ShieldCheck size={10} /> Você
                        </span>
                      )}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-4 py-3 text-gray-600">{a.email}</td>
                  <td className="px-4 py-3">
                    {level === "master" ? (
                      <span data-testid={`level-master-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-black px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white">
                        <Crown size={10} /> Master
                      </span>
                    ) : (
                      <span data-testid={`level-op-${a.id}`} className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-gray-700">
                        Operacional
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={a.active ? "text-green-600" : "text-gray-400"}>{a.active ? "Sim" : "Não"}</span>
                  </td>
                  {isMaster && (
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex gap-1">
                        <button
                          data-testid={`btn-edit-admin-${a.id}`}
                          onClick={() => openEdit(a)}
                          className="rounded-full border border-gray-200 p-2 hover:bg-gray-50"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          data-testid={`btn-del-admin-${a.id}`}
                          onClick={() => remove(a)}
                          disabled={isMe}
                          title={isMe ? "Você não pode excluir seu próprio acesso" : "Excluir"}
                          className="rounded-full border border-gray-200 p-2 hover:bg-red-50 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent data-testid="dialog-admin">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editingId ? "Editar recepcionista" : "Nova recepcionista"}
            </DialogTitle>
            <DialogDescription>
              Cadastre nome, e-mail, senha inicial e escolha o nível de acesso.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              <Field label="Nome">
                <input
                  data-testid="input-admin-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-black"
                />
              </Field>
              <Field label="Login / E-mail">
                <input
                  data-testid="input-admin-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-black"
                  type="email"
                />
              </Field>
              <Field label={editingId ? "Nova senha (opcional)" : "Senha inicial"}>
                <input
                  data-testid="input-admin-password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base outline-none focus:border-black"
                  type="text"
                />
              </Field>
              <Field label="Nível de acesso">
                <Select value={form.admin_level} onValueChange={(v) => setForm({ ...form, admin_level: v })}>
                  <SelectTrigger data-testid="select-admin-level">
                    <SelectValue placeholder="Escolha o nível" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operacional" data-testid="opt-level-op">
                      Operacional — opera a fila e cadastra profissionais
                    </SelectItem>
                    <SelectItem value="master" data-testid="opt-level-master">
                      Master — controle total (inclui gestão de recepcionistas)
                    </SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input
                  data-testid="input-admin-active"
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                />
                Ativa
              </label>
            </div>
          )}
          <DialogFooter className="gap-2">
            <button onClick={() => setForm(null)} className="rounded-full border border-gray-200 px-4 py-2 text-sm">
              Cancelar
            </button>
            <button
              data-testid="btn-save-admin"
              onClick={submit}
              className="rounded-full bg-black px-4 py-2 text-sm font-semibold text-white"
            >
              Salvar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-2 block text-xs font-medium uppercase tracking-wider text-gray-500">{label}</label>
      {children}
    </div>
  );
}
